import * as z from 'zod';

import { APP_ID_KEY } from '@/constants';
import config from '@config';
import { appIdQueryDocument, graphqlFetch } from '@shared/api';

/**
 * The tenant app's id in `system` (`{ app { id } }`), stamped on every
 * reader event as `app_id`. It never changes for an app, so a device asks
 * for it once and keeps it: the stored value survives deploys
 * (`PRESERVED_ON_DEPLOY_KEYS`) and records the app key it was fetched for,
 * so a different app served from the same origin never inherits it.
 */
export { APP_ID_KEY };

const storedSchema = z.object({ appKey: z.string(), id: z.string().min(1) });

let appId: string | undefined;
let loading: Promise<void> | undefined;

function readStored(): string | undefined {
	try {
		const stored = storedSchema.safeParse(
			JSON.parse(localStorage.getItem(APP_ID_KEY) ?? 'null'),
		);
		return stored.success && stored.data.appKey === config.build.appKey
			? stored.data.id
			: undefined;
	} catch {
		return undefined;
	}
}

/** The id if this device has it yet. Events recorded before then go without. */
export function getAppId(): string | undefined {
	appId ??= readStored();
	return appId;
}

/**
 * Fetches and stores the id unless the device already has it. Safe to call
 * repeatedly: concurrent calls share one request, and a failed one (offline,
 * server error) leaves nothing behind, so the next call tries again.
 */
export function loadAppId(): Promise<void> {
	if (getAppId() !== undefined) {
		return Promise.resolve();
	}
	loading ??= graphqlFetch(appIdQueryDocument)
		.then((data) => {
			const id = data.app?.id;
			if (!id) {
				return;
			}
			appId = `${id}`;
			try {
				localStorage.setItem(
					APP_ID_KEY,
					JSON.stringify({ appKey: config.build.appKey, id: appId }),
				);
			} catch {
				// Private mode etc.: kept for this session only.
			}
		})
		.catch(() => {
			// Already reported by graphqlFetch. Events keep going without it.
		})
		.finally(() => {
			loading = undefined;
		});
	return loading;
}

/**
 * The request for the id while it is in flight, else `undefined`. `track()`
 * holds what is recorded meanwhile, so a first launch's first events are
 * still stamped.
 */
export function pendingAppId(): Promise<void> | undefined {
	return loading;
}

/** Test-only: forget the id held in memory. */
export function resetAppIdForTests(): void {
	appId = undefined;
	loading = undefined;
}
